import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { preferencesService } from '../services/preferencesService';

const STORAGE_KEY = 'stackbd_language';

/**
 * Syncs language preference between localStorage, i18next, and the server.
 *
 * Flow:
 * 1. App renders immediately using localStorage/browser language (i18n.js handles this)
 * 2. After auth, fetches GET /v1/user/preferences
 * 3. If server language differs from current → updates i18next + localStorage
 */
export function useLanguageSync() {
  const { i18n } = useTranslation();
  const auth = useAuth();
  const hasSyncedRef = useRef(false);

  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user?.id_token || hasSyncedRef.current) return;

    const syncLanguage = async () => {
      try {
        const prefs = await preferencesService.getPreferences(auth.user.id_token);
        const serverLang = prefs.language;

        if (serverLang && serverLang !== i18n.language) {
          await i18n.changeLanguage(serverLang);
          localStorage.setItem(STORAGE_KEY, serverLang);
        }
      } catch (error) {
        // Silent fail — localStorage language is used as fallback
        console.debug('Language sync skipped:', error.message);
      } finally {
        hasSyncedRef.current = true;
      }
    };

    syncLanguage();
  }, [auth.isAuthenticated, auth.user?.id_token, i18n]);
}
